import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { Card } from '@/components/ui/Card';
import { relativeTime } from '@/lib/format';

interface Worker {
  worker_id: string;
  host: string;
  started_at: string;
  last_seen: string;
  status: string;
  capabilities: string | null;
  last_seen_seconds_ago: number | null;
}

interface Cooldown {
  company: string;
  next_allowed: string;
  reason: string | null;
}

interface Capacity {
  applicants_total: number;
  active_applicants: number;
  daily_cap_total: number;
  applied_today_total: number;
  remaining_today: number;
  utilization_pct: number;
  queue_depth: number;
  recent_7d_avg_per_day: number;
  effective_throughput_per_day: number;
  burndown_days_at_cap: number | null;
  burndown_days_at_actual: number | null;
  online_workers: number;
}

export default function WorkersPage() {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [cooldowns, setCooldowns] = useState<Cooldown[]>([]);
  const [capacity, setCapacity] = useState<Capacity | null>(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [w, c, cap] = await Promise.all([
        api.get<{ workers: Worker[] }>('/workers'),
        api.get<{ cooldowns: Cooldown[] }>('/workers/cooldowns'),
        api.get<Capacity>('/workers/capacity'),
      ]);
      setWorkers(w.workers);
      setCooldowns(c.cooldowns);
      setCapacity(cap);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    const h = setInterval(load, 30_000);
    return () => clearInterval(h);
  }, []);

  async function addCooldown(e: FormEvent) {
    e.preventDefault();
    if (!company) return;
    try {
      await api.post('/workers/cooldown', { company, minutes, reason: reason || undefined });
      toast.success(`Cooldown set on ${company} for ${minutes}m`);
      setCompany('');
      setReason('');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'cooldown_failed');
    }
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-tight">Workers</h2>

      {capacity && <CapacityCard cap={capacity} />}

      <Card title={`Active workers (${workers.length})`}>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : workers.length === 0 ? (
          <p className="text-sm text-gray-500">No workers registered. Start one on HOST_B.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="py-1.5">Worker ID</th>
                <th className="py-1.5">Host</th>
                <th className="py-1.5">Status</th>
                <th className="py-1.5">Last seen</th>
                <th className="py-1.5">Capabilities</th>
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => {
                const live = (w.last_seen_seconds_ago ?? 99999) < 120;
                const caps = w.capabilities ? safeJson(w.capabilities) : null;
                const apps = (caps && Array.isArray(caps.applicants)) ? caps.applicants.join(', ') : '—';
                return (
                  <tr key={w.worker_id} className="border-t border-gray-50">
                    <td className="py-1.5 font-medium">{w.worker_id}</td>
                    <td className="py-1.5 text-gray-600">{w.host}</td>
                    <td className="py-1.5">
                      <span className={live ? 'badge-green' : 'badge-red'}>
                        {live ? 'online' : 'stale'}
                      </span>
                    </td>
                    <td className="py-1.5 text-xs text-gray-500">
                      {relativeTime(w.last_seen)}{' '}
                      {w.last_seen_seconds_ago != null && (
                        <span className="text-gray-400">({w.last_seen_seconds_ago}s)</span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs text-gray-500">applicants: {apps}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={`Active cooldowns (${cooldowns.length})`}>
          {cooldowns.length === 0 ? (
            <p className="text-sm text-gray-500">No cooldowns active.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {cooldowns.map((c) => (
                <li key={c.company} className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">{c.company}</span>
                  <span className="text-xs text-gray-500">
                    until {relativeTime(c.next_allowed)} {c.reason && `· ${c.reason}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Add a cooldown">
          <form onSubmit={addCooldown} className="space-y-2">
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name (matches jobs.company)"
              className="input"
            />
            <div className="flex gap-2">
              <input
                type="number"
                value={minutes}
                onChange={(e) => setMinutes(+e.target.value)}
                min={1}
                max={60 * 24 * 30}
                className="input w-32"
              />
              <span className="self-center text-xs text-gray-500">minutes</span>
            </div>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              className="input"
            />
            <button type="submit" className="btn-primary">
              Add cooldown
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function safeJson(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function CapacityCard({ cap }: { cap: Capacity }) {
  const util = Math.min(100, cap.utilization_pct);
  const utilColor = util > 90 ? 'bg-red-500' : util > 70 ? 'bg-amber-500' : 'bg-green-500';
  const burndown =
    cap.burndown_days_at_actual ??
    cap.burndown_days_at_cap ??
    null;
  return (
    <Card title="Capacity planner">
      <div className="grid gap-3 text-sm md:grid-cols-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Today</div>
          <div className="mt-1 text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {cap.applied_today_total} / {cap.daily_cap_total}
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div className={`h-full ${utilColor}`} style={{ width: `${util}%` }} />
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {util.toFixed(1)}% utilization · {cap.remaining_today} slots left
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Throughput (7d avg)</div>
          <div className="mt-1 text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {cap.recent_7d_avg_per_day}<span className="text-base text-gray-500">/day</span>
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {cap.online_workers} online · {cap.active_applicants}/{cap.applicants_total} applicants active
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Queue burndown</div>
          <div className="mt-1 text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {burndown == null ? '—' : `${burndown} day${burndown === 1 ? '' : 's'}`}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {cap.queue_depth} queued · {cap.effective_throughput_per_day}/day effective
          </div>
        </div>
      </div>
    </Card>
  );
}
